import throttle from 'raf-throttle'
import {
  UICorePlugin,
  type Strings,
  type EventMap,
  template,
  type TemplateStringifier,
  type SupportedVersion,
  PlayerError,
  Browser
} from '@clappr/core'
import {Events} from 'common/types/events'
import {type Options, PLAY_IN_VISIBLE_AREA} from 'common/types/options'
import {timeStampToString} from 'utils/date'
import {
  getRecordsByFilter,
  getSuggest,
  type RecordsByFilterParams,
  type SuggestRecord
} from 'common/api/filters'
import {
  type SuggestConfig,
  AdvertisementBlockType,
  Errors
} from 'common/api/records'
import type {Core} from 'common/components/core'
import type {MediaControl} from 'common/plugins/media-control'
import type {
  Advertisement,
  AdvertisementEvent
} from 'common/plugins/advertisement'
import type {Viewport} from 'common/plugins/viewport'
import * as skins from './skins'

// use runtime symbols to prevent replacing keys by extensions
const ON_VISIBLE_AREA_CHANGED: unique symbol = Symbol('onVisibleAreaChanged')

export class Suggest<T extends Options> extends UICorePlugin<T> {
  public static SUGGEST_COUNT = 6
  public static MIN_TIMEOUT_TO_SHOW = 5

  public core!: Core<T>
  public enabled = true
  private playerState = ''
  private isVisible?: boolean
  private elementHeight?: number
  private endRecords?: SuggestRecord[]
  private pauseRecords?: SuggestRecord[]
  private renderedRecords?: SuggestRecord[]
  private $screenshots!: ZeptoCollection
  private $items?: ZeptoCollection

  private static template = ''
  private static classNames: Record<string, string> = {}
  private static externalLayout = false

  public static async preload(options: Options): Promise<void> {
    if (options.suggest && !Suggest.template) {
      const {suggest, mediaControl} = options
      const loadSkin =
        (suggest?.skinName && (skins as any)[suggest?.skinName]) ||
        (mediaControl?.skinName && (skins as any)[mediaControl?.skinName]) ||
        skins.classic

      if (typeof loadSkin !== 'function') {
        return
      }

      const {default: template, classNames, externalLayout} = await loadSkin()

      if (classNames) {
        Suggest.classNames = classNames
      }

      if (template) {
        Suggest.template = template
      }

      Suggest.externalLayout = !!externalLayout
    }
  }

  public get name(): string {
    return 'suggest'
  }

  public get supportedVersion(): SupportedVersion {
    return {min: '0.5.0'}
  }

  private get classNames(): Record<string, string> {
    return Suggest.classNames
  }

  public get template(): TemplateStringifier {
    return template(Suggest.template)
  }

  public get events(): EventMap {
    // TODO(VP-4598): Избавиться от использования классов для определения обработчика события
    // Использование дата аттрибутов имеет приоритет
    return {
      'click .eplayer-suggest-record, [data-id]': 'playNext',
      'click .eplayer-suggest-again, [data-suggest-replay]': 'playAgain',
      'click .eplayer-suggest-close, [data-suggest-close]': 'hide',
      'click [data-suggest-prev]': 'scrollToStart',
      'click [data-suggest-next]': 'scrollToEnd'
    }
  }

  public get i18n(): Strings<T> {
    return this.core.i18n
  }

  public get params(): SuggestConfig {
    return this.core.options.suggest || ({} as SuggestConfig)
  }

  public get height(): void | number {
    return this.elementHeight
  }

  private get mediaControl(): MediaControl<T> | null {
    return this.core.getPlugin('mediaControl') as MediaControl<T> | null
  }

  private get advertisement(): Advertisement<T> | null {
    return this.core.getPlugin('advertisement') as Advertisement<T> | null
  }

  private get viewport(): Viewport<T> | null {
    return this.core.getPlugin('viewport') as Viewport<T> | null
  }

  private get isRecordNotFound(): boolean {
    return Boolean(this.core.options.sources?.includes('not_found_fallback'))
  }

  public bindEvents(): void {
    this.listenTo(
      this.core,
      Events.CORE_ACTIVE_CONTAINER_CHANGED,
      this.onActiveContainerChange
    )

    this.listenTo(this.core, Events.CORE_READY, this.onReady)

    if (this.mediaControl) {
      this.listenTo(
        this.mediaControl,
        Events.MEDIACONTROL_RENDERED,
        this.render
      )
      this.listenTo(
        this.mediaControl,
        Events.MEDIACONTROL_SHARE,
        this.onMediaControlShare
      )
      this.listenTo(
        this.mediaControl,
        Events.MEDIACONTROL_MENU_CLICK,
        this.onMediaControlMenu
      )
    }

    if (this.viewport) {
      this.listenTo(
        this.viewport,
        Events.VIEWPORT_VISIBLE_AREA_CHANGED,
        this[ON_VISIBLE_AREA_CHANGED]
      )
    }

    if (this.advertisement) {
      this.listenTo(
        this.advertisement,
        Events.ADVERTISEMENT_LOADING,
        this.onAdLoading
      )
      this.listenTo(
        this.advertisement,
        Events.ADVERTISEMENT_LOADED,
        this.onAdLoaded
      )
    }

    const {activeContainer} = this.core

    if (activeContainer) {
      this.listenTo(activeContainer, Events.CONTAINER_PLAY, this.onPlay)
      this.listenTo(activeContainer, Events.CONTAINER_PAUSE, this.onPause)
      this.listenTo(activeContainer, Events.CONTAINER_STOP, this.onEnded)
      this.listenTo(activeContainer, Events.CONTAINER_ENDED, this.onEnded)

      const {url, urlPause} = this.params
      const multiPlayer = this.core.getPlugin('multiplayer')

      if (url && !multiPlayer) {
        this.listenTo(activeContainer, Events.CONTAINER_ENDED, this.showOnEnd)
      }

      if (urlPause) {
        this.listenTo(activeContainer, Events.CONTAINER_PAUSE, this.showOnPause)
      }
    }
  }

  private onReady(): void {
    if (this.isRecordNotFound) {
      this.render()
      this.onEnded()
      this.showOnEnd()
    }
  }

  private onActiveContainerChange(): void {
    this.stopListening()
    this.bindEvents()
  }

  public enable(): void {
    if (this.enabled) return
    this.enabled = true
    this.bindEvents()
  }

  public disable(): void {
    if (!this.enabled) return
    this.enabled = false
    this.stopListening()
  }

  // eslint-disable-next-line sonar/function-name
  private [ON_VISIBLE_AREA_CHANGED](visibleHeight: number): void {
    const {urlPause} = this.params
    const {activeContainer} = this.core
    const {
      autoPlay,
      minPlayingVisibleHeight = 0.5,
      [PLAY_IN_VISIBLE_AREA]: playInVisibleArea
    } = this.core.options
    const shouldToggle =
      (autoPlay || playInVisibleArea) && urlPause && activeContainer

    if (!shouldToggle) {
      return
    }

    const isVisible =
      !document.hidden &&
      (playInVisibleArea ? visibleHeight >= minPlayingVisibleHeight : true)

    if (isVisible === this.isVisible) {
      return
    }

    if (isVisible) {
      this.listenTo(activeContainer, Events.CONTAINER_PAUSE, this.showOnPause)
    } else {
      this.stopListening(
        activeContainer,
        Events.CONTAINER_PAUSE,
        this.showOnPause
      )
    }

    this.isVisible = isVisible
  }

  private onPlay(): void {
    this.playerState = 'playing'
  }

  private onPause(): void {
    this.playerState = 'paused'
  }

  private onEnded(): void {
    this.playerState = 'ended'
  }

  private async showOnPause(): Promise<void> {
    await new Promise((resolve) => window.setTimeout(resolve, 0))

    const {urlPause, filterIdPause} = this.params
    const {pauseSuggestFilterShuffle} = this.options

    if (!this.enabled || this.playerState !== 'paused' || !urlPause) {
      return
    }

    if (!this.pauseRecords) {
      try {
        if (filterIdPause) {
          const params: RecordsByFilterParams = {
            filterId: filterIdPause,
            pageLimit: 10,
            orderDir: pauseSuggestFilterShuffle ? 'random' : 'desc'
          }

          this.pauseRecords = await this.getRecordsByFilter(params, urlPause)
        } else if (urlPause) {
          this.pauseRecords = await this.getRecordsByUrl(urlPause)
        }

        this.trigger(Events.SUGGEST_LOADED, {url: urlPause})
      } catch {
        this.trigger(Events.SUGGEST_ERROR)
      }
    }

    // NOTE: За время загрузки рекомендаций состояние может измениться
    if (!this.enabled || this.playerState !== 'paused') {
      return
    }

    this.draw(this.pauseRecords, true)
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  private async showOnEnd(): Promise<void> {
    const {suggestFilterShuffle, replayTimeout, replay} = this.core.options

    if (
      replay &&
      (replayTimeout as number) < Suggest.MIN_TIMEOUT_TO_SHOW &&
      !this.isRecordNotFound
    ) {
      return
    }

    await new Promise((resolve) => window.setTimeout(resolve, 0))

    const {url, filterId} = this.params

    if (!this.enabled || this.playerState !== 'ended' || !url) {
      return
    }

    if (!this.endRecords) {
      try {
        if (filterId) {
          const params: RecordsByFilterParams = {
            filterId,
            pageLimit: 10,
            orderDir: suggestFilterShuffle ? 'random' : 'desc'
          }

          this.endRecords = await this.getRecordsByFilter(params, url)
        } else if (url) {
          this.endRecords = await this.getRecordsByUrl(url)
        }

        this.trigger(Events.SUGGEST_LOADED, {url})
      } catch {
        this.trigger(Events.SUGGEST_ERROR)
      }
    }

    if (this.isRecordNotFound && !this.endRecords?.length) {
      const formattedError = this.createError(
        {
          code: Errors.RECORD_NOT_FOUND.code,
          description: Errors.RECORD_NOT_FOUND.message,
          level: PlayerError.Levels.FATAL
        },
        {
          useCodePrefix: false
        }
      )

      this.trigger(Events.SUGGEST_ERROR, formattedError)

      return
    }

    // NOTE: За время загрузки рекомендаций состояние может измениться
    if (!this.enabled || this.playerState !== 'ended') {
      return
    }

    this.draw(this.endRecords, false)
  }

  private async getRecordsByUrl(suggestUrl: string): Promise<SuggestRecord[]> {
    const {
      url, // eslint-disable-line @typescript-eslint/no-unused-vars
      urlPause, // eslint-disable-line @typescript-eslint/no-unused-vars
      filterId, // eslint-disable-line @typescript-eslint/no-unused-vars
      filterIdPause, // eslint-disable-line @typescript-eslint/no-unused-vars
      ...rest
    } = this.params
    const records = await getSuggest(suggestUrl, rest)

    return records
      .filter((item) => item.is_processed && !!item.current_screenshot_small)
      .slice(0, Suggest.SUGGEST_COUNT)
  }

  private async getRecordsByFilter(
    params: RecordsByFilterParams,
    suggestUrl: string
  ): Promise<SuggestRecord[]> {
    try {
      const records = await getRecordsByFilter(params)

      return records
        .filter((item) => item.isProcessed && !!item.currentScreenshotSmall)
        .slice(0, Suggest.SUGGEST_COUNT)
        .map((item) => ({
          ...item.metadata,
          albums: item.albums,
          click_url: item.clickUrl || '',
          created_at: item.createdAt,
          current_screenshot: item.currentScreenshot,
          current_screenshot_small: item.currentScreenshotSmall || '',
          deleted_at: item.deletedAt,
          description: item.description,
          duration: item.duration,
          id: item.id,
          uuid: item.uuid,
          is_hd: item.isHd || false,
          is_processed: item.isProcessed,
          link: item.link,
          name: item.name,
          origin_size: item.originSize,
          screenshots: item.screenshots,
          tags: item.tags,
          updated_at: item.updatedAt,
          user_id: item.user_id,
          view_count: item.views
        }))
    } catch (error: any) {
      if (error.code === Errors.CACHE_NOT_READY.code && suggestUrl) {
        return await this.getRecordsByUrl(suggestUrl)
      } else {
        throw error
      }
    }
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  private draw(records: SuggestRecord[] | void, pause = false): void {
    if (!records || records.length === 0) {
      this.hide()

      return
    }

    if (records !== this.renderedRecords) {
      const normalizedRecords = records.map(({duration, ...rest}) => ({
        ...rest,
        ...(duration && {
          duration: timeStampToString(Number(duration))
        })
      }))
      const $el = $(
        this.template({
          records: normalizedRecords,
          replay: this.i18n.t('replay'),
          suggest: this.i18n.t('suggest')
        })
      )

      if (this.$el.children().length === 0) {
        this.$el
          .attr('class', $el.attr('class'))
          .css('display', $el.css('display'))
      }

      this.$el.empty().append($el.children())

      this.$items = this.$el.find('[data-suggest-items]')

      const {isPaused, isEnded, hideNames} = this.classNames

      // TODO(VP-4598): Избавиться от переключения видимости элементов
      // Использование классов имеет приоритет
      if (isPaused || isEnded || hideNames) {
        this.$el
          .toggleClass(isPaused, pause)
          .toggleClass(isEnded, !pause)
          .toggleClass(hideNames, !this.options.showSuggestNames)
      } else {
        const $closeButton = this.$el.find('.eplayer-suggest-close')
        const $replayButton = this.$el.find('.eplayer-suggest-again')

        if ($closeButton.length) {
          $closeButton.toggle(pause)
          $replayButton.toggle(!pause)
        }
      }

      this.renderedRecords = records
    }

    this.$screenshots = this.$el.find('.eplayer-suggest-record > img')
    this.$screenshots.forEach((item) =>
      item?.addEventListener('load', this.onScreenshotLoaded(item))
    )
    this.$el.show()

    if (this.renderedRecords?.length && this.$items?.length) {
      this.$items.on('scroll', this.toggleArrowsVisibility)
      this.listenTo(this.core, Events.CORE_RESIZE, this.toggleArrowsVisibility)

      this.$items.css('scroll-behavior', 'auto')
      this.$items.get(0).scrollLeft = 0
      this.$items.css('scroll-behavior', null)
      this.toggleArrowsVisibility()
    }

    const toggleControls = this.$el.hasClass('eplayer-skin-shadow')

    this.elementHeight = this.$el.height()
    this.trigger(Events.SUGGEST_RENDERED, toggleControls)

    const {activeContainer} = this.core

    if (activeContainer) {
      this.listenTo(activeContainer, Events.CONTAINER_PLAY, this.hide)
    }
  }

  protected toggleArrowsVisibility = throttle((): void => {
    const {classNames, $items} = this
    const scrollElement = $items?.get(0)

    if (!scrollElement) {
      return
    }

    this.$el.toggleClass(
      classNames.leftArrowVisible,
      scrollElement.scrollLeft > 0
    )

    this.$el.toggleClass(
      classNames.rightArrowVisible,
      scrollElement.scrollLeft + scrollElement.offsetWidth <
        scrollElement.scrollWidth
    )
  })

  protected scrollToStart(): void {
    this.scrollByStep(-1)
  }

  protected scrollToEnd(): void {
    this.scrollByStep(1)
  }

  private scrollByStep(step: 1 | -1): void {
    const scrollElement = this.$items?.get(0)
    const $items = this.$items?.children()
    const $firstItem = $items?.eq(0)
    const $secondItem = $items?.eq(1)

    if (!scrollElement || !$firstItem?.length || !$secondItem?.length) {
      return
    }

    const itemWidth = $secondItem.offset().left - $firstItem.offset().left

    scrollElement.scrollLeft += step * itemWidth
  }

  protected hide(): void {
    const {activeContainer} = this.core

    if (this.isRecordNotFound) {
      return
    }

    this.$items?.off('scroll', this.toggleArrowsVisibility)
    this.stopListening(
      this.core,
      Events.CORE_RESIZE,
      this.toggleArrowsVisibility
    )
    this.$el.hide()
    delete this.elementHeight
    this.trigger(Events.SUGGEST_CLOSE)

    if (activeContainer) {
      this.stopListening(activeContainer, Events.CONTAINER_PLAY, this.hide)
    }
  }

  private onMediaControlMenu(): void {
    const menu = this.core.getPlugin('settingsMenu')

    if (menu && this.$el.is(':visible') && Browser.isMobile) {
      this.listenToOnce(menu, Events.SETTINGS_MENU_CLOSE, () => {
        this.draw(this.renderedRecords)
      })
      this.hide()
    }
  }

  private onMediaControlShare(): void {
    const share = this.core.getPlugin('share')
    const {urlPause} = this.params
    const {activeContainer} = this.core

    if (!share) {
      return
    }

    if (activeContainer && urlPause) {
      this.stopListening(
        activeContainer,
        Events.CONTAINER_PAUSE,
        this.showOnPause
      )
      this.listenToOnce(share, Events.SHARE_CLOSE, () => {
        this.listenTo(activeContainer, Events.CONTAINER_PAUSE, this.showOnPause)
      })
    }

    if (this.$el.is(':visible')) {
      this.listenToOnce(share, Events.SHARE_CLOSE, () => {
        this.draw(this.renderedRecords)
      })
      this.hide()
    }
  }

  private onScreenshotLoaded = (elem: any) => () => {
    $(elem).css(
      'object-fit',
      elem?.naturalWidth >= elem?.naturalHeight ? 'cover' : 'contain'
    )
  }

  private onAdLoading(event: AdvertisementEvent): void {
    const {url, urlPause} = this.params
    const multiPlayer = this.core.getPlugin('multiplayer')
    const onEnd =
      url &&
      !multiPlayer &&
      event.blockType === AdvertisementBlockType.POST_ROLL
    const onPause =
      urlPause && event.blockType === AdvertisementBlockType.PAUSE_BANNER

    if (onEnd || onPause) {
      this.disable()
      this.listenToOnce(
        this.advertisement,
        Events.ADVERTISEMENT_CONTENT_RESUME_REQUESTED,
        () => {
          this.enable()

          if (onEnd) {
            this.showOnEnd()
          } else {
            this.showOnPause()
          }
        }
      )
    }
  }

  private onAdLoaded(): void {
    this.disable()

    if (this.$el.is(':visible')) this.hide()

    this.listenToOnce(
      this.advertisement,
      Events.ADVERTISEMENT_CONTENT_RESUME_REQUESTED,
      this.enable
    )
  }

  protected playNext(event: MouseEvent): void {
    const {id, url} = (event.currentTarget as HTMLElement).dataset
    const {redirectToSuggestOnClick} = this.core.options

    if (url && redirectToSuggestOnClick) {
      this.hide()
      window.open(url)

      return
    }

    const item =
      id && this.renderedRecords?.find((item) => item.id === Number(id))

    if (!item) {
      return
    }

    this.trigger(Events.SUGGEST_CLICK)
    this.core.reload({id: item.uuid, autoPlay: true} as T)
  }

  protected playAgain(): void {
    this.core.activeContainer?.play()
  }

  public render(): this {
    if (this.isRecordNotFound) {
      const $activeContainer = this.core.activeContainer.$el

      $activeContainer.before(this.$el)

      return this
    }

    const $el = this.mediaControl?.$el

    if (Suggest.externalLayout) {
      $el?.after(this.el)
    } else {
      $el?.append(this.el)
    }

    return this
  }
}
